import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Cascader, Checkbox, Divider, Form, Input, Spin } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDsInquiry from '../hook/useDsInquiry';
import { ROUTER } from '../../../constants/router';
import { useLocation, useNavigate } from 'react-router-dom';
import useQuery from '../../../util/useQuery';
import classNames from 'classnames';

const CheckboxGroup = Checkbox.Group;

export default function Filter({ statusId, departmentId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const optionSortby = [
        {
            value: 'dataSubjectName',
            label: t('Data Subject Type'),
            children: [
                {
                    value: 'ASC',
                    label: t('Ascending'),
                },
                {
                    value: 'DESC',
                    label: t('Descending'),
                },
            ],
        },
    ];

    const {
        getDataSubject,
        exportDataFlow,
        exportRopa,
        dataSubject,
        loading,
        loadingExport,
    } = useDsInquiry(() => {});

    const [values, setValues] = useState({
        sortby: ['dataSubjectName', 'ASC'],
        search: '',
    });
    const [checkedList, setCheckedList] = useState([]);
    const [indeterminate, setIndeterminate] = useState(false);
    const [checkAll, setCheckAll] = useState(false);

    const onChange = (list) => {
        setCheckedList(list);
        setIndeterminate(
            !!list.length && list.length < filterDataSubject(dataSubject).length
        );
        setCheckAll(list.length === filterDataSubject(dataSubject).length);
    };

    const onCheckAllChange = (e) => {
        setCheckedList(
            e.target.checked
                ? filterDataSubject(dataSubject)?.map(
                      (item) => item?.dataSubjectId
                  )
                : []
        );
        setIndeterminate(false);
        setCheckAll(e.target.checked);
    };

    const [form] = Form.useForm();

    useEffect(() => {
        getDataSubject();
    }, [getDataSubject]);

    const params = useQuery();

    // Sort dataSubjectName in dataSubject with character
    const sortDataSubject = (dataSubject) => {
        const [sortby, order] = values?.sortby || [];
        return dataSubject.sort((a, b) => {
            if (a[sortby] < b[sortby]) {
                return order === 'ASC' ? -1 : 1;
            }
            if (a[sortby] > b[sortby]) {
                return order === 'ASC' ? 1 : -1;
            }
            return 0;
        });
    };

    const filterDataSubject = (dataSubject) => {
        if (!values?.search) return sortDataSubject(dataSubject);
        // to lower case
        const search = values?.search.toLowerCase();
        const filter = dataSubject.filter((item) =>
            item?.dataSubjectName.toLowerCase().includes(search)
        );
        return sortDataSubject(filter) || [];
    };

    const { hash } = useLocation();

    useEffect(() => {
        const hash_ = hash || '#dataflow';
        if (params.get('id')) {
            const dataSubjectId = dataSubject.find(
                (item) => item?.dataSubjectId === params.get('id')
            );
            if (!dataSubjectId) {
                navigate(ROUTER.DATA_INQUIRY.DEFAULT + hash_);
            }
        }
        if (!params.get('id') && dataSubject.length > 0) {
            if (dataSubject[0]?.dataSubjectId)
                navigate(
                    ROUTER.DATA_INQUIRY.DEFAULT +
                        `?id=${dataSubject[0]?.dataSubjectId}` +
                        hash_
                );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSubject, params.get('id')]);

    // Reset all checklist when change hash
    useEffect(() => {
        setCheckedList([]);
        setIndeterminate(false);
        setCheckAll(false);
        const hash_ = hash || '#dataflow';
        if (dataSubject[0]?.dataSubjectId)
            navigate(
                ROUTER.DATA_INQUIRY.DEFAULT +
                    `?id=${dataSubject[0]?.dataSubjectId}` +
                    hash_
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSubject, hash]);

    const handleExport = () => {
        if (hash === '#dataflow')
            exportDataFlow({
                dataSubjectIds: checkedList.join(','),
                departmentId,
                statusId,
            });
        if (hash === '#ropa')
            exportRopa({
                dataSubjectIds: checkedList.join(','),
                departmentId,
                statusId,
            });
    };

    return (
        <div className="bg-white col-span-2 rounded-lg h-max">
            <Form
                layout="vertical"
                form={form}
                className="px-4 pt-4"
                onValuesChange={(_, value) => setValues(value)}
                initialValues={{
                    sortby: ['dataSubjectName', 'ASC'],
                    search: '',
                }}
            >
                <FormItem
                    label={
                        <span className="font-semibold text-base">
                            {t('Search Filter')}
                        </span>
                    }
                    name="sortby"
                    className="mb-2"
                >
                    <Cascader
                        className="w-full"
                        allowClear={false}
                        size="large"
                        options={optionSortby}
                        showArrow
                    />
                </FormItem>
                <FormItem name="search" noStyle>
                    <Input
                        allowClear
                        placeholder={t('Search for data subject type')}
                        size="large"
                        prefix={<SearchOutlined />}
                        onChange={(e) => {
                            setCheckedList([]);
                            setIndeterminate(false);
                            setCheckAll(false);
                        }}
                    />
                </FormItem>
            </Form>
            <Divider />
            <div className="px-4 pb-4">
                <div className="font-semibold text-xl">
                    {t('Data Subject Type')}
                </div>
                <div className="flex justify-between items-center mt-4">
                    <div className="flex space-x-2 items-center">
                        <Checkbox
                            indeterminate={indeterminate}
                            onChange={onCheckAllChange}
                            checked={checkAll}
                            className="font-semibold"
                        >
                            {t('Select All')} ({checkedList?.length})
                        </Checkbox>
                        <Button
                            loading={loadingExport}
                            onClick={handleExport}
                            disabled={!checkedList.length > 0}
                            type="primary"
                            icon={<DownloadOutlined />}
                        />
                    </div>
                    <div className="font-semibold text-gray-400">
                        {t('TotalItems', {
                            length: filterDataSubject(dataSubject)?.length,
                        })}
                    </div>
                </div>
            </div>
            <div
                className="h-[60vh] overflow-auto overflow-x-hidden relative"
                style={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                }}
            >
                <Spin spinning={loading}>
                    <CheckboxGroup
                        value={checkedList}
                        onChange={onChange}
                        className="w-full"
                    >
                        {filterDataSubject(dataSubject).map((item) => {
                            const active =
                                params.get('id') === item?.dataSubjectId;
                            return (
                                <div
                                    key={item?.dataSubjectId}
                                    className={classNames(
                                        'hover:bg-[#F8F4FF] duration-300 w-full relative flex items-center',
                                        {
                                            'bg-[#F8F4FF]': active,
                                        }
                                    )}
                                    style={{
                                        borderBottom:
                                            '1px solid rgba(0, 0, 0, 0.06)',
                                    }}
                                >
                                    {active && (
                                        <div className="w-[6px] h-full bg-purple-PHX absolute left-0 top-0 rounded-tr-lg rounded-br-lg" />
                                    )}
                                    <Checkbox
                                        className="p-4"
                                        value={item?.dataSubjectId}
                                    ></Checkbox>
                                    <button
                                        className="text-left w-full py-4 font-semibold"
                                        onClick={() => {
                                            navigate(
                                                ROUTER.DATA_INQUIRY.DEFAULT +
                                                    `?id=${item?.dataSubjectId}${hash}`
                                            );
                                        }}
                                        type="button"
                                        key={item?.dataSubjectId}
                                    >
                                        {item?.dataSubjectName}
                                    </button>
                                </div>
                            );
                        })}
                    </CheckboxGroup>
                </Spin>
            </div>
        </div>
    );
}
